import "./styles/main.css";
import { Navbar } from "./components/Navbar.js";
import { TransactionForm } from "./components/TransactionForm.js";
import { ReportsPage } from "./components/ReportsPage.js";
import { SettingsPage } from "./components/SettingsPage.js";

/**
 * Main Application
 * Multi-page accounting system with premium liquid glass UI
 */

class App {
  constructor() {
    this.currentPage = "";
    this.navbar = new Navbar();
    this.transactionForm = new TransactionForm();
    this.reportsPage = new ReportsPage();
    this.settingsPage = new SettingsPage();
  }

  /**
   * Initialize the application
   */
  async initialize() {
    try {
      const appContainer = document.getElementById("app");
      if (!appContainer) {
        console.error("App container not found");
        return;
      }

      appContainer.innerHTML = `
        <div class="app-container">
          <!-- Navbar Container -->
          <div id="navbarContainer"></div> 
          
          <main class="main-content" id="mainContent">
            ${this.renderLoading()}
          </main>

          <!-- Footer -->
          <footer class="app-footer">
            <div class="footer-content">
              <p>© ${new Date().getFullYear()} Report by Mr. Aitthisone.D All Rights Reserved.</p>
              <p class="footer-contact">Tel: +85620 788 666 95</p>
            </div>
          </footer>
        </div>
      `;

      // Initialize navbar separately to avoid overwrite
      const navContainer = document.getElementById("navbarContainer");
      if (navContainer) navContainer.innerHTML = this.navbar.render();

      this.navbar.initialize();
      this.navbar.setNavigationCallback((page) => this.navigateToPage(page));

      // Handle browser back/forward navigation
      window.addEventListener("popstate", (event) => {
        const page = event.state?.page || this.getPageFromUrl();
        this.navigateToPage(page, false);
      });

      // Load initial page based on URL
      const initialPage = this.getPageFromUrl();
      await this.navigateToPage(initialPage, true);
    } catch (error) {
      console.error("Error initializing app:", error);
      this.showError("ເກີດຄວາມຜິດພາດໃນການໂຫຼດລະບົບ");
    }
  }

  getPageFromUrl() {
    const path = window.location.pathname;
    if (path.includes("reports")) return "reports";
    if (path.includes("settings")) return "settings";
    return "reports"; // Default
  }

  /**
   * Render premium loading spinner
   */
  renderLoading() {
    return `
      <div class="loading-overlay">
        <div class="liquid-spinner"></div>
        <div class="loading-text">ທິບພະລັງສີ ກຳລັງໂຫຼດ...</div>
      </div>
    `;
  }

  /**
   * Navigate to a page
   * @param {string} page - Page name
   * @param {boolean} updateHistory - Whether to push to history
   */
  async navigateToPage(page, updateHistory = true) {
    if (this.currentPage === page && page !== "reports") return;

    this.currentPage = page;
    const mainContent = document.getElementById("mainContent");
    if (!mainContent) return;

    // Update Browser URL
    if (updateHistory) {
      const url = page === "entry" ? "/" : `/${page}`;
      window.history.pushState({ page }, "", url);
    }

    // Update Navbar Active State
    this.updateNavbarActiveState(page);

    // Show loading with liquid animation
    mainContent.innerHTML = this.renderLoading();

    try {
      // Force a slight delay for smooth transition and to show the WOW spinner
      await new Promise((resolve) => setTimeout(resolve, 500));

      switch (page) {
        case "entry":
          await this.loadTransactionFormPage(mainContent);
          break;
        case "reports":
          await this.loadReportsPage(mainContent);
          break;
        case "settings":
          await this.loadSettingsPage(mainContent);
          break;
        default:
          await this.loadTransactionFormPage(mainContent);
      }
    } catch (error) {
      console.error(`Error loading ${page} page:`, error);
      mainContent.innerHTML = `<div class="glass-panel error-page">ເກີດຄວາມຜິດພາດ: ${error.message}</div>`;
    }
  }

  updateNavbarActiveState(page) {
    const links = document.querySelectorAll(".nav-item");
    links.forEach((link) => {
      link.classList.remove("active");
      if (
        link.getAttribute("href") === "#" + page ||
        (page === "entry" && link.getAttribute("href") === "#entry")
      ) {
        link.classList.add("active");
      }
    });
  }

  /**
   * Load transaction form page
   */
  async loadTransactionFormPage(container) {
    await this.transactionForm.loadMetadata();
    container.innerHTML = this.transactionForm.render();
    this.transactionForm.initialize();
  }

  /**
   * Load reports page
   */
  async loadReportsPage(container) {
    container.innerHTML = this.reportsPage.render();
    await this.reportsPage.initialize();
  }

  /**
   * Load settings page
   */
  async loadSettingsPage(container) {
    await this.settingsPage.loadMetadata();
    container.innerHTML = this.settingsPage.render();
    this.settingsPage.initialize();
  }

  /**
   * Show error message
   */
  showError(message) {
    const mainContent = document.getElementById("mainContent");
    if (mainContent) {
      mainContent.innerHTML = `
        <div class="page-container">
          <div class="glass-panel error-page">
            <h2>⚠️ ເກີດຄວາມຜິດພາດ</h2>
            <p>${message}</p>
            <button class="btn btn-primary" onclick="location.reload()" style="margin-top: 1rem">ໂຫຼດໃໝ່</button>
          </div>
        </div>
      `;
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const app = new App();
  app.initialize();
});
